/**
 * BudgetManagementPage
 * Admin page for managing user and role budgets
 */
import { useState, useMemo } from 'react'
import { Search, DollarSign, Users } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useUserBudgetList, useBudgetRoles } from '@/hooks/useBudgetAdmin'
import { BudgetProgress } from '@/components/budget/BudgetProgress'
import { getBudgetStatus } from '@/types/budget'
import {
  formatBudgetUsd,
  getBudgetStatusColor,
} from '@/types/budgetAdmin'
import type { UserBudgetInfo, BudgetRoleInfo } from '@/types/budgetAdmin'
import { useDebounce } from '@/hooks/useDebounce'
import { UserBudgetOverrideDialog } from '@/components/budget/UserBudgetOverrideDialog'
import { RoleBudgetEditDialog } from '@/components/budget/RoleBudgetEditDialog'

// ============================================================================
// Loading Skeleton
// ============================================================================

function BudgetTableSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-4 p-4">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32 flex-1" />
          <Skeleton className="h-4 w-20" />
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// User Budgets Tab
// ============================================================================

interface UserBudgetsTabProps {
  data: { items: UserBudgetInfo[]; totalCount: number } | undefined
  isLoading: boolean
  error: Error | null
  search: string
  onSearchChange: (value: string) => void
  roleFilter: string
  onRoleFilterChange: (value: string) => void
  roles: BudgetRoleInfo[]
}

function UserBudgetsTab({
  data,
  isLoading,
  error,
  search,
  onSearchChange,
  roleFilter,
  onRoleFilterChange,
  roles,
}: UserBudgetsTabProps) {
  const [selectedUser, setSelectedUser] = useState<UserBudgetInfo | null>(null)

  // Filter users client-side (backend doesn't have search yet)
  const filteredUsers = useMemo(() => {
    if (!data?.items) return []
    let filtered = data.items
    
    if (search) {
      const lowerSearch = search.toLowerCase()
      filtered = filtered.filter(
        (u) =>
          u.displayName.toLowerCase().includes(lowerSearch) ||
          u.email.toLowerCase().includes(lowerSearch)
      )
    }
    
    if (roleFilter !== 'all') {
      filtered = filtered.filter((u) => u.roleCode === roleFilter)
    }
    
    return filtered
  }, [data?.items, search, roleFilter])

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={roleFilter} onValueChange={onRoleFilterChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {roles.map((role) => (
              <SelectItem key={role.id} value={role.id}>
                {role.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Error State */}
      {error && (
        <div className="text-center py-8 text-muted-foreground">
          <p>Failed to load user budgets.</p>
          <p className="text-sm">Please try again later.</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && <BudgetTableSkeleton />}

      {/* Empty State */}
      {!isLoading && !error && filteredUsers.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p>No users found</p>
          <p className="text-sm">Try adjusting your search or filters.</p>
        </div>
      )}

      {/* User Budget Table */}
      {!isLoading && !error && filteredUsers.length > 0 && (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Budget Limit</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="w-48">Usage</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => {
                  const isUnlimited = user.effectiveBudgetUsd === null
                  const colorClass = getBudgetStatusColor(
                    user.percentUsed,
                    isUnlimited
                  )

                  return (
                    <TableRow key={user.oid}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{user.displayName}</div>
                          <div className="text-sm text-muted-foreground">
                            {user.email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{user.roleCode}</Badge>
                      </TableCell>
                      <TableCell className={colorClass}>
                        {formatBudgetUsd(user.effectiveBudgetUsd)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            user.budgetSource === 'user-override'
                              ? 'default'
                              : 'secondary'
                          }
                        >
                          {user.budgetSource === 'user-override'
                            ? 'Override'
                            : user.budgetSource === 'role-default'
                            ? 'Role'
                            : 'Unlimited'}
                        </Badge>
                        {user.hasOverride && user.overrideReason && (
                          <span
                            className="ml-2 text-xs text-muted-foreground"
                            title={user.overrideReason}
                          >
                            ({user.overrideReason.slice(0, 20)}
                            {user.overrideReason.length > 20 ? '...' : ''})
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isUnlimited ? (
                          <span className="text-sm text-gray-500">
                            ${user.currentMonthUsageUsd.toFixed(2)} used
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <BudgetProgress
                              percentUsed={user.percentUsed}
                              status={getBudgetStatus(user.percentUsed, isUnlimited)}
                              className="flex-1"
                              showLabel={false}
                              size="sm"
                            />
                            <span className="text-xs text-muted-foreground w-12 text-right">
                              {user.percentUsed.toFixed(0)}%
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedUser(user)}
                        >
                          {user.hasOverride ? 'Edit Override' : 'Set Override'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>

          {/* Footer with count */}
          <div className="text-sm text-muted-foreground">
            Showing {filteredUsers.length} of {data?.totalCount ?? 0} users
          </div>
        </>
      )}

      {/* Override Dialog */}
      {selectedUser && (
        <UserBudgetOverrideDialog
          open={!!selectedUser}
          onOpenChange={(open) => !open && setSelectedUser(null)}
          user={selectedUser}
        />
      )}
    </div>
  )
}

// ============================================================================
// Role Budgets Tab
// ============================================================================

interface RoleBudgetsTabProps {
  roles: BudgetRoleInfo[]
  isLoading: boolean
  error: Error | null
}

function RoleBudgetsTab({ roles, isLoading, error }: RoleBudgetsTabProps) {
  const [selectedRole, setSelectedRole] = useState<BudgetRoleInfo | null>(null)

  if (isLoading) {
    return <BudgetTableSkeleton />
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>Failed to load roles.</p>
        <p className="text-sm">Please try again later.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead>Monthly Budget</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell className="font-medium">{role.displayName}</TableCell>
                <TableCell
                  className={
                    role.monthlyBudgetUsd === null
                      ? 'text-gray-500'
                      : 'text-green-600'
                  }
                >
                  {formatBudgetUsd(role.monthlyBudgetUsd)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedRole(role)}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Role Edit Dialog */}
      {selectedRole && (
        <RoleBudgetEditDialog
          open={!!selectedRole}
          onOpenChange={(open) => !open && setSelectedRole(null)}
          role={selectedRole}
        />
      )}
    </div>
  )
}

// ============================================================================
// Main Page Component
// ============================================================================

export function BudgetManagementPage() {
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [activeTab, setActiveTab] = useState('users')

  const debouncedSearch = useDebounce(search, 300)

  const {
    data: userBudgets,
    isLoading: usersLoading,
    error: usersError,
  } = useUserBudgetList({ page: 1, pageSize: 100 })

  const {
    data: roles,
    isLoading: rolesLoading,
    error: rolesError,
  } = useBudgetRoles()

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="h-6 w-6" />
            Budget Management
          </h1>
          <p className="text-muted-foreground">
            Manage user and role budget limits
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              User Budgets
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Role Budgets
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-4">
            <UserBudgetsTab
              data={userBudgets}
              isLoading={usersLoading}
              error={usersError}
              search={debouncedSearch}
              onSearchChange={setSearch}
              roleFilter={roleFilter}
              onRoleFilterChange={setRoleFilter}
              roles={roles ?? []}
            />
          </TabsContent>

          <TabsContent value="roles" className="mt-4">
            <RoleBudgetsTab
              roles={roles ?? []}
              isLoading={rolesLoading}
              error={rolesError}
            />
          </TabsContent>
        </Tabs>
      </div>
    </PageShell>
  )
}
